import { range } from "lodash";

// ---------- In project imports ----------

import { Button, ButtonGroup, HGap } from "./UI";
import { PlayerSummary } from "./Common";

import Styles from './Field.module.scss';
import SharedStyles from './Shared.module.scss';

import * as Game from './copied/Game';
import * as GameLogic from './copied/gameLogic';

const { min, max, abs, floor, ceil, round } = Math;

function Field (
	{ state, actionLauncher, uid }:
	{
		state: Game.PlayingState,
		actionLauncher: (action: Game.Action, confirmationMessage?: string) => () => Promise<void>,
		uid: string
	}
) {
	const gamePlaying = state.type === 'started';

	const me = state.users[uid];
	const myPos = me.pos;
	const meDead = me.health <= 0;

	const EmptyCell = ({ pos }: { pos: Game.Pos }) => {
		const dist = GameLogic.distance(myPos, pos);
		const walkable =
			dist <= me.actionPoints
			&& state.type === 'started'; // Can't do anything when the game is finished

		let classes = Styles.cell;
		if (walkable)
			classes += ' ' + Styles.walkable;

		return <td className={classes}
			onClick={
				walkable ?
					actionLauncher({
						type: Game.ActionType.Move,
						actorId: '',
						to: pos,
					})
				: undefined
			}
		>
			{walkable ? dist : ''}
		</td>;
	}

	const OccupiedCell = (
		{ pos, cellUser, cellUserId } :
		{
			pos: Game.Pos,
			cellUser: Game.Player,
			cellUserId: string,
		}
	) => {
		const dist = GameLogic.distance(myPos, pos);
			
		const myCell = dist === 0;
		const reachable = 0 < dist && dist <= me.range;
		const dead = cellUser.health <= 0;

		let classes = Styles.cell;
		if (myCell)
			classes += ' ' + Styles.my;
		if (reachable && gamePlaying)
			classes += ' ' + Styles.reachable;

		return <td className={classes}>
			<div className={Styles.deadBase + (dead ? ' ' + Styles.dead : '')}>💀</div>
			<div className={Styles.cellNickname}>{cellUser.nickname}</div>
			<div className={Styles.occupiedInfo}>
				{ myCell ?
					<PlayerSummary user={cellUser} />
				: meDead ?
				<>
					<PlayerSummary user={cellUser} />
					{!dead && gamePlaying &&
					<div className={SharedStyles.verticalAlign}>
						Vote:
						{me.votesAvailable <= 0
						? ' no votes left'
						: <>
							<HGap /><ButtonGroup>{
								range(1, 1+me.votesAvailable, 1)
								.map(i =>
								<Button key={i} label={i} onClick={actionLauncher({
									type: Game.ActionType.Vote,
									actorId: '',
									receiverId: cellUserId,
									count: i,
								})} />
							)}</ButtonGroup>
						</>
						}
					</div>
					}
				</>
				: !reachable ?
				<div>
					<PlayerSummary user={cellUser} />
					{gamePlaying &&
					<>
						<div>Not in your range</div>
						<div>Your range is {me.range}</div>
						<div>This cell is {dist} cells away</div>
					</>}
				</div>
				: <>
					<PlayerSummary user={cellUser} />
					{gamePlaying
					&& <>
						{!dead && me.actionPoints >= 1 &&
						<>
							<div className={SharedStyles.verticalAlign}>
								Attack: <HGap /> <ButtonGroup>{
									range(1, 1+min(me.actionPoints, cellUser.health), 1)
									.map(i =>
									<Button key={i} label={i} onClick={actionLauncher({
										type: Game.ActionType.Shoot,
										actorId: '',
										shootedPlayerId: cellUserId,
										count: i,
										to: pos,
									})} />
								)}</ButtonGroup>
							</div>
							<div className={SharedStyles.verticalAlign}>
								Gift points: <HGap /> <ButtonGroup>{
									range(1, 1+me.actionPoints, 1)
									.map(i =>
									<Button key={i} label={i} onClick={actionLauncher({
										type: Game.ActionType.Transfer,
										actorId: '',
										receiverId: cellUserId,
										actionPoints: i,
										healthPoints: 0,
									})} />
								)}</ButtonGroup>
							</div>
						</>}
						<div className={SharedStyles.verticalAlign}>
							Gift health: <HGap /> <ButtonGroup>{
								range(1, me.health, 1)
								.map(i =>
								<Button key={i} label={i} onClick={actionLauncher({
									type: Game.ActionType.Transfer,
									actorId: '',
									receiverId: cellUserId,
									actionPoints: 0,
									healthPoints: i,
								})} />
							)}</ButtonGroup>
						</div>
					</>
					}
				</>
				}
			</div>
		</td>
	}

	return (
	<table>
		<tbody>
			{range(state.height).map(y =>
			<tr key={y}>
				{range(state.width).map(x => {
					const pos = { x, y };

					const cellContent = Object.entries(state.users)
						.find(u => GameLogic.posEqual(u[1].pos, pos));

					return cellContent == undefined
						? <EmptyCell key={pos.y + '-' + pos.x} pos={pos} />
						: <OccupiedCell key={pos.y + '-' + pos.x} pos={pos} cellUser={cellContent[1]} cellUserId={cellContent[0]} />;
				})}
			</tr>)}
		</tbody>
	</table>
	);
}

export default Field;
